// pubmedExportRows.ts
import type { DuplicateInfo } from '../PubMedUtilities'
import type { EnrichedPubMedArticle } from './pubmedEnrichment'
import type { KeywordCategoryKey, PubMedSearchParams } from '../types'
import { extractReviewFields } from './pubmedReviewFieldExtractor'

export interface PubMedTlrReviewRow {
    searchId: number
    pmid: string
    searchTermsScore: string | number
    customKeywordsScore: string | number

    titleScreening: string
    abstractScreening: string
    fullTextScreening: string

    atlasMatch: string
    atlasCollection: string
    atlasMatchedOn: string

    sponsorCompany: string

    title: string
    authors: string
    authorAffiliations: string
    year: string | number
    citation: string
    publicationTypes: string
    abstract: string
    drugsInterventions: string
    manualSearchOutcomes: string

    epidemiology: string
    epidemiologyMeasures: string
    epidemiologyThresholds: string

    clinicalEfficacy: string
    clinicalEfficacyMeasures: string
    clinicalEfficacyThresholds: string

    humanistic: string
    humanisticMeasures: string
    humanisticThresholds: string

    economic: string
    economicMeasures: string
    economicThresholds: string

    treatmentPatterns: string
    treatmentPatternsMeasures: string
    treatmentPatternsThresholds: string

    safety: string
    safetyMeasures: string
    safetyThresholds: string

    keywordHierarchy: string
    keywordMatchedTerms: string
    keywordMatchedFields: string

    objective: string
    messagingConclusion: string

    doi: string
    pmcId: string
    fullTextLink: string
}

export interface PubMedExtractionLabRow {
    searchId: number
    pmid: string
    pmcId: string
    doi: string
    title: string
    citation: string
    fullTextLink: string
    abstract: string
    sponsorCompany: string
    authors: string
    authorAffiliations: string

    studyDesignSentence: string
    studyDesignTags: string

    primaryOutcomeSentence: string
    secondaryOutcomeSentence: string
    exploratoryOutcomeSentence: string
    outcomeSentences: string

    studyPopulationSentence: string

    sampleSizeSentence: string
    sampleSizeValue: string
    totalSampleSize: string
    groupSampleSizes: string

    inclusionCriteriaSentence: string
    exclusionCriteriaSentence: string
    ageSentence: string

    costAdjustmentSentence: string
    costCurrencyYears: string

    economicOutcomeSentences: string
    costResultSentences: string
    costTypeTags: string
    resourceUseSentences: string
    resourceUseTags: string
    costEffectivenessSentence: string
    icerSentence: string
    icerValue: string
    budgetImpactSentence: string
    budgetImpactValue: string
    pmpmPppmSentence: string
    pmpmPppmValue: string
    inpatientCostSentence: string
    outpatientCostSentence: string
    pharmacyCostSentence: string

    dataSourceSentence: string
    dataSourceTypes: string

    comparatorSentence: string
    followUpSentence: string

    timePeriodSentence: string
    timePeriodStart: string
    timePeriodEnd: string

    studyDurationSentence: string
    studyDurationValue: string

    objectiveSentence: string
    conclusionSentence: string

    geographySentence: string
    settingSentence: string
    realWorldEvidenceSentence: string
    proHrqolSentence: string
    safetySentence: string
}

export interface PubMedConfigRow {
    section: string
    key: string
    value: string
}

export interface PubMedDashboardMetricRow {
    metric: string
    value: string | number
}

export interface PubMedExportWorkbookData {
    tlrRows: PubMedTlrReviewRow[]
    extractionRows: PubMedExtractionLabRow[]
    configRows: PubMedConfigRow[]
    summaryMetrics: PubMedDashboardMetricRow[]
    shareOfVoiceRows: Array<Record<string, string | number>>
    shareOfVoiceByYearRows: Array<Record<string, string | number>>
    categoryByCompanyRows: Array<Record<string, string | number>>
    outcomeByCompanyRows: Array<Record<string, string | number>>
    messagingByCompanyRows: Array<Record<string, string>>
    summarySheet?: {
        companyName?: string
        orgName?: string
        searchString?: string
        diseaseArea?: string
        interventions?: string
        outcomes?: string
        dateRange?: string
        articleTypes?: string
        exportedAt?: string
        orgLogoData?: string
        arysanaLogoData?: string
    }
}

type BuildWorkbookOptions = {
    params?: PubMedSearchParams | null
    queryString?: string
    generatedAt?: string
    companyName?: string
    orgName?: string
    orgLogoData?: string
    arysanaLogoData?: string
}

const CATEGORY_ORDER: KeywordCategoryKey[] = [
    'epidemiology',
    'clinicalEfficacy',
    'humanistic',
    'economic',
    'treatmentPatterns',
    'safety',
]

const CATEGORY_LABELS: Record<KeywordCategoryKey, string> = {
    epidemiology: 'Epidemiology and Disease Burden',
    clinicalEfficacy: 'Clinical Efficacy and Effectiveness',
    humanistic: 'Patient-Reported and Humanistic Outcomes',
    economic: 'Economic Outcomes',
    treatmentPatterns: 'Treatment Patterns',
    safety: 'Safety',
    competitors: 'Competitor Treatments',
}

type StructuredKeywordExport = {
    hierarchy: string
    matchedTerms: string
    matchedFields: string
}

type CategoryStructuredKeywordExport = {
    measures: string
    thresholds: string
}

function uniqueStrings(values: Array<string | undefined | null>): string[] {
    return Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => Boolean(value))))
}

function categoryLabel(category: KeywordCategoryKey): string {
    return CATEGORY_LABELS[category] || category
}

function normalizeAuthors(authors: unknown): string {
    if (!authors) return ''
    if (Array.isArray(authors)) return authors.join(', ')
    return String(authors)
}

function normalizePublicationTypes(publicationTypes: unknown): string {
    if (!publicationTypes) return ''
    if (Array.isArray(publicationTypes)) return publicationTypes.join('; ')
    return String(publicationTypes)
}

function joinList(values: unknown): string {
    if (!values) return ''
    if (Array.isArray(values)) return values.filter(Boolean).join('; ')
    return String(values)
}

function getShortCitation(article: EnrichedPubMedArticle): string {
    return (article as any).shortCitation || (article as any).citation || article.journal || ''
}

function buildFullTextLink(article: EnrichedPubMedArticle): string {
    if (article.pmcId) {
        return `https://www.ncbi.nlm.nih.gov/pmc/articles/${article.pmcId}/`
    }
    if ((article as any).link) return (article as any).link
    if (article.doi) return `https://doi.org/${article.doi}`
    if (article.pmid) return `https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`
    return ''
}

function toAtlasMatchValue(duplicateInfo?: DuplicateInfo): string {
    return duplicateInfo?.isDuplicate ? 'Y' : '-'
}

function toCategoryFlag(hit: boolean | undefined): string {
    return hit ? 'Y' : '-'
}

function buildCategoryStructuredKeywordExport(
    article: EnrichedPubMedArticle,
    category: KeywordCategoryKey
): CategoryStructuredKeywordExport {
    const matches = (article.matchedKeywordFamilies || []).filter((match) => match.category === category)

    const measures = uniqueStrings(matches.map((match) => match.measureLabel || match.familyLabel))

    const thresholds = uniqueStrings(matches.map((match) => match.thresholdLabel))

    return {
        measures: measures.join('; '),
        thresholds: thresholds.join('; '),
    }
}

function buildStructuredKeywordExport(article: EnrichedPubMedArticle): StructuredKeywordExport {
    const matches = article.matchedKeywordFamilies || []

    if (matches.length === 0) {
        return {
            hierarchy: '',
            matchedTerms: '',
            matchedFields: '',
        }
    }

    const matchedTerms = uniqueStrings(matches.flatMap((match) => match.matchedTerms || []))

    const matchedFields = uniqueStrings(matches.flatMap((match) => match.matchedFields || []))

    const hierarchyPaths = uniqueStrings(
        matches.map((match) => {
            const category = categoryLabel(match.category)

            const measure = match.measureLabel || match.familyLabel

            const pathParts = [category]

            if (measure) {
                pathParts.push(measure)
            }

            if (match.thresholdLabel && match.thresholdLabel !== measure) {
                pathParts.push(match.thresholdLabel)
            }

            return pathParts.join(' > ')
        })
    )

    return {
        hierarchy: hierarchyPaths.join('\n'),
        matchedTerms: matchedTerms.join('; '),
        matchedFields: matchedFields.join('; '),
    }
}

function sponsorCompanies(article: EnrichedPubMedArticle): string {
    return joinList(article.industryAffiliations)
}

function authorAffiliations(article: EnrichedPubMedArticle): string {
    return joinList(article.otherAffiliations)
}

function formatArticleTypeSelection(params?: PubMedSearchParams | null): string {
    if (!params?.articleTypes) return ''

    const map: Record<string, string> = {
        booksAndDocuments: 'Books and Documents',
        clinicalTrial: 'Clinical Trial',
        metaAnalysis: 'Meta-Analysis',
        randomizedControlledTrial: 'Randomized Controlled Trial',
        review: 'Review',
        systematicReview: 'Systematic Review',
    }

    return Object.entries(params.articleTypes)
        .filter(([, selected]) => selected)
        .map(([key]) => map[key] || key)
        .join('; ')
}

function aggregateMatchedKeywords(
    articles: EnrichedPubMedArticle[],
    category: KeywordCategoryKey
): Array<{ keyword: string; articleCount: number }> {
    const counts = new Map<string, number>()

    for (const article of articles) {
        const matches = article.keywordHits?.[category]?.matches || []
        const uniqueMatches = Array.from(new Set(matches))

        for (const match of uniqueMatches) {
            counts.set(match, (counts.get(match) || 0) + 1)
        }
    }

    return Array.from(counts.entries())
        .map(([keyword, articleCount]) => ({ keyword, articleCount }))
        .sort((a, b) => b.articleCount - a.articleCount || a.keyword.localeCompare(b.keyword))
}

function buildCompanyArticleCounts(articles: EnrichedPubMedArticle[]): Map<string, number> {
    const counts = new Map<string, number>()

    for (const article of articles) {
        const companies = Array.isArray(article.industryAffiliations) ? article.industryAffiliations : []
        const uniqueCompanies = Array.from(new Set(companies))

        for (const company of uniqueCompanies) {
            counts.set(company, (counts.get(company) || 0) + 1)
        }
    }

    return counts
}

function buildCompanyList(articles: EnrichedPubMedArticle[]): string[] {
    const counts = buildCompanyArticleCounts(articles)

    return Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([company]) => company)
}

function buildShareOfVoiceByYearRows(articles: EnrichedPubMedArticle[]): Array<Record<string, string | number>> {
    const companies = buildCompanyList(articles)

    const years = Array.from(
        new Set(articles.map((article) => String(article.year || '').trim()).filter((year) => /^\d{4}$/.test(year)))
    ).sort((a, b) => Number(a) - Number(b))

    return companies.map((company) => {
        const companyArticles = articles.filter(
            (article) => Array.isArray(article.industryAffiliations) && article.industryAffiliations.includes(company)
        )

        const row: Record<string, string | number> = {
            company,
            totalStudies: companyArticles.length,
        }

        for (const year of years) {
            row[year] = companyArticles.filter((article) => String(article.year || '') === year).length
        }

        return row
    })
}

function buildCategoryByCompanyRows(articles: EnrichedPubMedArticle[]): Array<Record<string, string | number>> {
    const companies = buildCompanyList(articles)

    return CATEGORY_ORDER.map((category) => {
        const row: Record<string, string | number> = {
            category: category,
        }

        for (const company of companies) {
            row[company] = 0
        }

        for (const article of articles) {
            const hit = article.keywordHits?.[category]?.hit
            if (!hit) continue

            const articleCompanies = Array.isArray(article.industryAffiliations) ? article.industryAffiliations : []
            const uniqueCompanies = Array.from(new Set(articleCompanies))

            for (const company of uniqueCompanies) {
                row[company] = Number(row[company] || 0) + 1
            }
        }

        return row
    })
}

function buildOutcomeByCompanyRows(articles: EnrichedPubMedArticle[]): Array<Record<string, string | number>> {
    const companies = buildCompanyList(articles)
    const rows: Array<Record<string, string | number>> = []

    for (const category of CATEGORY_ORDER) {
        const outcomeCounts = new Map<string, Map<string, number>>()

        for (const article of articles) {
            const matches = article.keywordHits?.[category]?.matches || []
            const articleCompanies = Array.isArray(article.industryAffiliations) ? article.industryAffiliations : []
            const uniqueCompanies = Array.from(new Set(articleCompanies))
            const uniqueMatches = Array.from(new Set(matches))

            for (const outcome of uniqueMatches) {
                if (!outcomeCounts.has(outcome)) {
                    outcomeCounts.set(outcome, new Map<string, number>())
                }

                const companyMap = outcomeCounts.get(outcome)!
                for (const company of uniqueCompanies) {
                    companyMap.set(company, (companyMap.get(company) || 0) + 1)
                }
            }
        }

        const sortedOutcomes = Array.from(outcomeCounts.keys()).sort((a, b) => a.localeCompare(b))

        for (const outcome of sortedOutcomes) {
            const row: Record<string, string | number> = {
                category,
                outcome,
            }

            for (const company of companies) {
                row[company] = outcomeCounts.get(outcome)?.get(company) || 0
            }

            rows.push(row)
        }
    }

    {
        const category = 'manual search'
        const outcomeCounts = new Map<string, Map<string, number>>()

        for (const article of articles) {
            const curatedMatches = new Set<string>()

            for (const curatedCategory of CATEGORY_ORDER) {
                const matches = article.keywordHits?.[curatedCategory]?.matches || []
                for (const match of matches) {
                    curatedMatches.add(match)
                }
            }

            const matches = (article.outcomeMatches || []).filter((match) => !curatedMatches.has(match))

            const articleCompanies = Array.isArray(article.industryAffiliations) ? article.industryAffiliations : []
            const uniqueCompanies = Array.from(new Set(articleCompanies))
            const uniqueMatches = Array.from(new Set(matches))

            for (const outcome of uniqueMatches) {
                if (!outcomeCounts.has(outcome)) {
                    outcomeCounts.set(outcome, new Map<string, number>())
                }

                const companyMap = outcomeCounts.get(outcome)!
                for (const company of uniqueCompanies) {
                    companyMap.set(company, (companyMap.get(company) || 0) + 1)
                }
            }
        }

        const sortedOutcomes = Array.from(outcomeCounts.keys()).sort((a, b) => a.localeCompare(b))

        for (const outcome of sortedOutcomes) {
            const row: Record<string, string | number> = {
                category,
                outcome,
            }

            for (const company of companies) {
                row[company] = outcomeCounts.get(outcome)?.get(company) || 0
            }

            rows.push(row)
        }
    }
    return rows
}

function buildMessagingByCompanyRows(articles: EnrichedPubMedArticle[]): Array<Record<string, string>> {
    const messagesByCompany = new Map<string, string[]>()

    for (const article of articles) {
        const companies = Array.isArray(article.industryAffiliations)
            ? Array.from(new Set(article.industryAffiliations))
            : []

        const extraction = extractReviewFields({
            title: article.title,
            abstract: article.abstract,
        })

        const message = extraction.conclusionSentence || ''

        if (!message) continue
        if (!companies.length) continue

        for (const company of companies) {
            if (!messagesByCompany.has(company)) {
                messagesByCompany.set(company, [])
            }

            const existing = messagesByCompany.get(company)!
            if (!existing.includes(message)) {
                existing.push(message)
            }
        }
    }

    const maxMessages = Math.max(0, ...Array.from(messagesByCompany.values()).map((arr) => arr.length))

    return Array.from(messagesByCompany.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([company, messages]) => {
            const row: Record<string, string> = { company }

            for (let i = 0; i < maxMessages; i++) {
                row[`message_${i + 1}`] = messages[i] || ''
            }

            return row
        })
}

export function buildTlrReviewRows(
    articles: EnrichedPubMedArticle[],
    duplicateMap: Record<string, DuplicateInfo> = {}
): PubMedTlrReviewRow[] {
    return articles.map((article, index) => {
        const extraction = extractReviewFields({
            title: article.title,
            abstract: article.abstract,
        })

        const duplicateInfo = duplicateMap[article.pmid]

        const epidemiologyKeywords = buildCategoryStructuredKeywordExport(article, 'epidemiology')

        const clinicalEfficacyKeywords = buildCategoryStructuredKeywordExport(article, 'clinicalEfficacy')

        const humanisticKeywords = buildCategoryStructuredKeywordExport(article, 'humanistic')

        const economicKeywords = buildCategoryStructuredKeywordExport(article, 'economic')

        const treatmentPatternsKeywords = buildCategoryStructuredKeywordExport(article, 'treatmentPatterns')

        const safetyKeywords = buildCategoryStructuredKeywordExport(article, 'safety')

        const structuredKeywords = buildStructuredKeywordExport(article)

        return {
            searchId: index + 1,
            pmid: article.pmid || '',
            searchTermsScore: article.searchTermsScore ?? '',
            customKeywordsScore: article.customKeywordsScore ?? '',

            titleScreening: '-',
            abstractScreening: '-',
            fullTextScreening: '-',

            atlasMatch: toAtlasMatchValue(duplicateInfo),
            atlasCollection: duplicateInfo?.collection || '',
            atlasMatchedOn: duplicateInfo?.matchedOn || '',

            sponsorCompany: sponsorCompanies(article),

            title: article.title || '',
            authors: normalizeAuthors(article.authors),
            authorAffiliations: authorAffiliations(article),
            year: article.year ?? '',
            citation: getShortCitation(article),
            publicationTypes: normalizePublicationTypes(article.publicationTypes),
            abstract: article.abstract || '',
            drugsInterventions: joinList(article.interventionMatches),
            manualSearchOutcomes: joinList(article.outcomeMatches),

            epidemiology: toCategoryFlag(article.keywordHits?.epidemiology?.hit),
            epidemiologyMeasures: epidemiologyKeywords.measures,
            epidemiologyThresholds: epidemiologyKeywords.thresholds,

            clinicalEfficacy: toCategoryFlag(article.keywordHits?.clinicalEfficacy?.hit),
            clinicalEfficacyMeasures: clinicalEfficacyKeywords.measures,
            clinicalEfficacyThresholds: clinicalEfficacyKeywords.thresholds,

            humanistic: toCategoryFlag(article.keywordHits?.humanistic?.hit),
            humanisticMeasures: humanisticKeywords.measures,
            humanisticThresholds: humanisticKeywords.thresholds,

            economic: toCategoryFlag(article.keywordHits?.economic?.hit),
            economicMeasures: economicKeywords.measures,
            economicThresholds: economicKeywords.thresholds,

            treatmentPatterns: toCategoryFlag(article.keywordHits?.treatmentPatterns?.hit),
            treatmentPatternsMeasures: treatmentPatternsKeywords.measures,
            treatmentPatternsThresholds: treatmentPatternsKeywords.thresholds,

            safety: toCategoryFlag(article.keywordHits?.safety?.hit),
            safetyMeasures: safetyKeywords.measures,
            safetyThresholds: safetyKeywords.thresholds,

            keywordHierarchy: structuredKeywords.hierarchy,
            keywordMatchedTerms: structuredKeywords.matchedTerms,
            keywordMatchedFields: structuredKeywords.matchedFields,

            objective: extraction.objectiveSentence || '',
            messagingConclusion: extraction.conclusionSentence || '',

            doi: article.doi || '',
            pmcId: article.pmcId || '',
            fullTextLink: buildFullTextLink(article),
        }
    })
}

export function buildExtractionLabRows(articles: EnrichedPubMedArticle[]): PubMedExtractionLabRow[] {
    return articles.map((article, index) => {
        const extraction = extractReviewFields({
            title: article.title,
            abstract: article.abstract,
        })

        return {
            searchId: index + 1,
            pmid: article.pmid || '',
            pmcId: article.pmcId || '',
            doi: article.doi || '',
            title: article.title || '',
            citation: getShortCitation(article),
            fullTextLink: buildFullTextLink(article),
            abstract: article.abstract || '',
            sponsorCompany: sponsorCompanies(article),
            authors: normalizeAuthors(article.authors),
            authorAffiliations: authorAffiliations(article),

            ...extraction,
        }
    })
}

export function buildConfigRows(
    articles: EnrichedPubMedArticle[],
    options: BuildWorkbookOptions = {}
): PubMedConfigRow[] {
    const { params, queryString = '', generatedAt = '' } = options
    const rows: PubMedConfigRow[] = []

    rows.push(
        { section: 'Search Inputs', key: 'Disease Area / Conditions', value: params?.diseaseArea || '' },
        { section: 'Search Inputs', key: 'Interventions', value: params?.interventions || '' },
        { section: 'Search Inputs', key: 'Sponsor', value: params?.sponsor || '' },
        { section: 'Search Inputs', key: 'Outcomes', value: params?.outcomes || '' },
        { section: 'Search Inputs', key: 'Authors', value: params?.authors || '' },
        {
            section: 'Search Inputs',
            key: 'Start Date',
            value: params?.startDate ? String(params.startDate.format('YYYY-MM')) : '',
        },
        {
            section: 'Search Inputs',
            key: 'End Date',
            value: params?.endDate ? String(params.endDate.format('YYYY-MM')) : '',
        },
        {
            section: 'Search Inputs',
            key: 'Article Types',
            value: formatArticleTypeSelection(params),
        },
        {
            section: 'Search Inputs',
            key: 'Open Access / PMC only',
            value: params?.openAccessOnly ? 'Yes' : 'No',
        },
        { section: 'Search Inputs', key: 'PubMed Query', value: queryString },
        { section: 'Search Inputs', key: 'Generated At', value: generatedAt },
        { section: 'Search Inputs', key: 'Articles Exported', value: String(articles.length) }
    )

    const interventionMatches = new Map<string, number>()
    for (const article of articles) {
        const uniqueMatches = Array.from(new Set(article.interventionMatches || []))
        for (const match of uniqueMatches) {
            interventionMatches.set(match, (interventionMatches.get(match) || 0) + 1)
        }
    }

    for (const [keyword, count] of Array.from(interventionMatches.entries()).sort((a, b) => b[1] - a[1])) {
        rows.push({
            section: 'Matched Intervention Terms',
            key: keyword,
            value: String(count),
        })
    }

    for (const category of CATEGORY_ORDER) {
        const keywordRows = aggregateMatchedKeywords(articles, category)

        for (const item of keywordRows) {
            rows.push({
                section: `Matched Keywords - ${category}`,
                key: item.keyword,
                value: String(item.articleCount),
            })
        }
    }

    return rows
}

export function buildSummaryMetrics(
    articles: EnrichedPubMedArticle[],
    duplicateMap: Record<string, DuplicateInfo> = {}
): PubMedDashboardMetricRow[] {
    const sponsorTaggedCount = articles.filter(
        (article) => Array.isArray(article.industryAffiliations) && article.industryAffiliations.length > 0
    ).length

    const atlasMatchCount = articles.filter((article) => duplicateMap[article.pmid]?.isDuplicate).length

    const rows: PubMedDashboardMetricRow[] = [
        { metric: 'Total Exported Articles', value: articles.length },
        { metric: 'ATLAS Matches', value: atlasMatchCount },
        { metric: 'Articles with Sponsor Company', value: sponsorTaggedCount },
    ]

    for (const category of CATEGORY_ORDER) {
        const count = articles.filter((article) => article.keywordHits?.[category]?.hit).length
        rows.push({
            metric: `Articles tagged: ${category}`,
            value: count,
        })
    }

    return rows
}

export function buildShareOfVoiceRows(articles: EnrichedPubMedArticle[]): Array<Record<string, string | number>> {
    const counts = buildCompanyArticleCounts(articles)

    return Array.from(counts.entries())
        .map(([company, articleCount]) => ({
            company,
            articleCount,
        }))
        .sort(
            (a, b) =>
                Number(b.articleCount) - Number(a.articleCount) || String(a.company).localeCompare(String(b.company))
        )
}

export function buildPubMedExportWorkbookData(
    articles: EnrichedPubMedArticle[],
    duplicateMap: Record<string, DuplicateInfo> = {},
    options: BuildWorkbookOptions = {}
): PubMedExportWorkbookData {
    const startDate =
        options.params?.startDate && typeof options.params.startDate.format === 'function'
            ? options.params.startDate.format('YYYY-MM')
            : ''
    const endDate =
        options.params?.endDate && typeof options.params.endDate.format === 'function'
            ? options.params.endDate.format('YYYY-MM')
            : ''

    const dateRange =
        startDate || endDate ? `${startDate || ''}${startDate || endDate ? ' to ' : ''}${endDate || ''}` : ''

    const articleTypes = options.params?.articleTypes
        ? Object.entries(options.params.articleTypes)
              .filter(([, value]) => !!value)
              .map(([key]) => key)
              .join(', ')
        : ''

    return {
        tlrRows: buildTlrReviewRows(articles, duplicateMap),
        extractionRows: buildExtractionLabRows(articles),
        configRows: buildConfigRows(articles, options),
        summaryMetrics: buildSummaryMetrics(articles, duplicateMap),
        shareOfVoiceRows: buildShareOfVoiceRows(articles),
        shareOfVoiceByYearRows: buildShareOfVoiceByYearRows(articles),
        categoryByCompanyRows: buildCategoryByCompanyRows(articles),
        outcomeByCompanyRows: buildOutcomeByCompanyRows(articles),
        messagingByCompanyRows: buildMessagingByCompanyRows(articles),
        summarySheet: {
            companyName: options.companyName || 'Arysana',
            orgName: options.orgName || '',
            searchString: options.queryString || '',
            diseaseArea: options.params?.diseaseArea || '',
            interventions: options.params?.interventions || '',
            outcomes: options.params?.outcomes || '',
            dateRange,
            articleTypes,
            exportedAt: options.generatedAt || new Date().toLocaleString(),
            orgLogoData: options.orgLogoData || '',
            arysanaLogoData: options.arysanaLogoData || '',
        },
    }
}
